import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Footprints, Users, GraduationCap, Flag, CalendarDays, Dumbbell } from 'lucide-react';
import { MONTHS_FULL, DOW, parseISO, monthMatrix } from '../../lib/dates';
import { isSCSession } from '../../lib/sessionInfo';

const TYPE_ICON = { 'Run Session': Footprints, 'Classes': Dumbbell, 'Social': Users, 'Workshop': GraduationCap, 'Event': Flag, 'Parkrun': Footprints };
function TypeIcon({ type }) { const I = TYPE_ICON[type] || CalendarDays; return <I size={13} strokeWidth={2.1} />; }

export default function BookCalendar({ sessions, selected, onSelect, onPickSession }) {
  const firstIso = useMemo(() => sessions.map(s => s.dateISO).filter(Boolean).sort()[0], [sessions]);
  const base = firstIso ? parseISO(firstIso) : new Date();
  const [cur, setCur] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const cells = useMemo(() => monthMatrix(cur.y, cur.m), [cur]);
  const today = new Date();

  const byDay = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      const dt = parseISO(s.dateISO);
      if (dt && dt.getFullYear() === cur.y && dt.getMonth() === cur.m) {
        (map[dt.getDate()] = map[dt.getDate()] || []).push(s);
      }
    });
    return map;
  }, [sessions, cur]);

  const move = (delta) => {
    let m = cur.m + delta, y = cur.y;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCur({ y, m });
  };
  const goToday = () => setCur({ y: today.getFullYear(), m: today.getMonth() });

  return (
    <div className="calendar card" data-testid="book-calendar">
      <div className="cal-head">
        <div className="cal-title">
          <button className="cal-nav" data-testid="cal-prev" onClick={() => move(-1)} aria-label="Previous month"><ChevronLeft size={18} /></button>
          <strong data-testid="cal-month-label">{MONTHS_FULL[cur.m]} {cur.y}</strong>
          <button className="cal-nav" data-testid="cal-next" onClick={() => move(1)} aria-label="Next month"><ChevronRight size={18} /></button>
        </div>
        <button className="btn secondary cal-today" data-testid="cal-today" onClick={goToday}>Today</button>
      </div>
      <div className="cal-dow">{DOW.map(d => <span key={d}>{d}</span>)}</div>
      <div className="cal-grid">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="cal-cell empty" />;
          const evts = byDay[d] || [];
          const has = evts.length > 0;
          const isToday = today.getFullYear() === cur.y && today.getMonth() === cur.m && today.getDate() === d;
          const isSel = selected && selected.y === cur.y && selected.m === cur.m && selected.d === d;
          return (
            <div key={i} data-testid={`cal-cell-${d}`} className={`cal-cell ${has ? 'has' : ''} ${isSel ? 'sel' : ''} ${isToday ? 'today' : ''}`}
              onClick={() => has && onSelect(isSel ? null : { y: cur.y, m: cur.m, d })}>
              <span className="cal-num">{d}{isToday && <em>Today</em>}</span>
              {has && (
                <div className="cal-events">
                  {evts.slice(0, 2).map(s => {
                    const free = !s.price || /free/i.test(s.price);
                    return (
                      <span key={s.id} data-testid={`cal-chip-${s.id}`} className={`cal-chip ${free ? 'free' : 'paid'}`} title={`${s.title} · ${s.time}`}
                        onClick={(e) => { e.stopPropagation(); onPickSession && onPickSession(s); }}>
                        <TypeIcon type={s.type} /><span className="cal-chip-label">{s.title}</span>{isSCSession(s) && <span className="cal-chip-sc" data-testid={`cal-chip-sc-${s.id}`}>!</span>}
                      </span>
                    );
                  })}
                  {evts.length > 2 && <span className="cal-more" onClick={(e) => { e.stopPropagation(); onSelect({ y: cur.y, m: cur.m, d }); }}>+{evts.length - 2} more</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selected && <button className="btn ghost cal-clear" data-testid="cal-clear" onClick={() => onSelect(null)}>Show all sessions</button>}
    </div>
  );
}
